import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from '../Paths/Api';

const SendInvites = () => {
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState('');
  const [attendees, setAttendees] = useState([{ name: '', email: '', phone: '', role: '' }]);
  const [csvFile, setCsvFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [showCsvPopup, setShowCsvPopup] = useState(false);
  const [inputMode, setInputMode] = useState('manual'); // 'manual' or 'csv'
  const navigate = useNavigate();
  const phone = localStorage.getItem('phone');

  // Logging helper
  const logWithTimestamp = (message, data = null) => {
    const timestamp = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    console.log(`[${timestamp}] ${message}`, data ? data : '');
  };

  useEffect(() => {
    const fetchMeetings = async () => {
      logWithTimestamp('Fetching meetings from API');
      try {
        const phone = localStorage.getItem('phone');
        const user_id = localStorage.getItem('userId'); // ✅ get user_id from localStorage

        if (!phone || !user_id) {
          logWithTimestamp('Phone or user ID not found in local storage');
          setError('Phone or user ID not found in local storage');
          return;
        }

        // ✅ send both phone and user_id
        const response = await fetch(
          `${ApiURl}/fetchMeetings.php?phone=${encodeURIComponent(phone)}&user_id=${encodeURIComponent(user_id)}`,
          {
            method: 'GET',
          }
        );

        logWithTimestamp('Fetch meetings response', { status: response.status, ok: response.ok });
        const data = await response.json();
        logWithTimestamp('Fetch meetings JSON data', data);

        if (data.success) {
          setMeetings(data.meetings);
        } else {
          setError(data.message || 'Failed to fetch meetings');
          logWithTimestamp('Fetch meetings error', data.message);
        }
      } catch (err) {
        logWithTimestamp('Fetch meetings error', err.message);
        setError('An error occurred while fetching meetings.');
      }
    };
    fetchMeetings();
  }, []);


  const handleMeetingChange = (e) => {
    logWithTimestamp('Meeting selected', e.target.value);
    setSelectedMeeting(e.target.value);
  };

  const handleAttendeeChange = (index, e) => {
    const { name, value } = e.target;
    logWithTimestamp(`Attendee field changed - Index: ${index}, Field: ${name}`, value);
    const newAttendees = [...attendees];
    newAttendees[index][name] = value;
    setAttendees(newAttendees);
  };

  const addAttendee = () => {
    logWithTimestamp('Adding new attendee');
    setAttendees([...attendees, { name: '', email: '', phone: '', role: '' }]);
  };

  const removeAttendee = (index) => {
    logWithTimestamp('Removing attendee', index);
    setAttendees(attendees.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    logWithTimestamp('CSV file selected', file ? file.name : 'No file');
    if (file) {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        logWithTimestamp('CSV file content', text.slice(0, 200)); // Log first 200 chars
        const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim()));
        const headers = rows[0].map(h => h.toLowerCase());
        if (!['name', 'email', 'phone', 'role'].every(h => headers.includes(h))) {
          setError('CSV must contain Name, Email, Phone, and Role columns');
          setCsvFile(null);
          logWithTimestamp('Invalid CSV headers', headers);
          return;
        }
        const data = rows.slice(1).filter(row => row.length >= 4).map(row => ({
          name: row[headers.indexOf('name')],
          email: row[headers.indexOf('email')],
          phone: row[headers.indexOf('phone')],
          role: row[headers.indexOf('role')],
        }));
        logWithTimestamp('CSV parsed data', data);
        setCsvPreview(data);
        setShowCsvPopup(true);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    logWithTimestamp('Downloading CSV template');
    const csvContent = "Name,Email,Phone,Role\nJohn Doe,john@example.com,1234567890,Attendee\nJane Smith,jane@example.com,0987654321,Speaker";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'attendees_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const confirmCsvUpload = async () => {
    logWithTimestamp('Confirming CSV upload');
    setShowCsvPopup(false);
    await submitForm(csvPreview);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    logWithTimestamp('Form submission started');
    if (!selectedMeeting) {
      setError('Please select a meeting');
      logWithTimestamp('Validation error: No meeting selected');
      return;
    }
    if (inputMode === 'csv' && csvFile) {
      logWithTimestamp('CSV mode: Waiting for confirmation');
      return;
    }
    const filteredAttendees = attendees.filter(a => a.email && a.phone && a.role);
    logWithTimestamp('Submitting manual attendees', filteredAttendees);
    await submitForm(filteredAttendees);
  };

  const submitForm = async (attendeesData) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    logWithTimestamp('submitForm started', { meeting_id: selectedMeeting, inputMode, attendeesData });

    const formData = new FormData();
    formData.append('meeting_id', selectedMeeting);

    if (inputMode === 'csv' && csvFile) {
      logWithTimestamp('Appending CSV file', csvFile.name);
      formData.append('attendees_csv', csvFile);
    } else {
      logWithTimestamp('Appending JSON attendees', attendeesData);
      formData.append('attendees', JSON.stringify(attendeesData));
    }

    try {
      logWithTimestamp('Sending fetch request to send_invites.php', { url: `${ApiURl}/send_invites.php` });
      const response = await fetch(`${ApiURl}/send_invites.php`, {
        method: 'POST',
        body: formData,
      });

      logWithTimestamp('Fetch response received', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      const responseText = await response.text();
      logWithTimestamp('Raw response text', responseText.slice(0, 500));

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}, Response: ${responseText.slice(0, 200)}`);
      }

      let data;
      try {
        data = JSON.parse(responseText);
        logWithTimestamp('Parsed JSON data', data);
      } catch (jsonError) {
        logWithTimestamp('JSON parse error', jsonError.message);
        throw new Error(`Invalid JSON response: ${responseText.slice(0, 200)}`);
      }

      if (data.success) {
        setSuccess('Invitations sent successfully!');
        setAttendees([{ name: '', email: '', phone: '', role: '' }]);
        setCsvFile(null);
        setCsvPreview(null);
        setInputMode('manual');
        logWithTimestamp('Success: Invitations sent');
      } else {
        setError(data.message || 'Failed to send invitations');
        logWithTimestamp('API error', data.message);
      }
    } catch (err) {
      logWithTimestamp('submitForm error', err.message);
      setError(`An error occurred: ${err.message}`);
    } finally {
      setIsSubmitting(false);
      logWithTimestamp('submitForm completed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-3 shadow-lg fixed w-full z-20">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-3 md:hidden focus:outline-none hover:bg-teal-600 p-2 rounded-full transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-lg font-bold tracking-tight">Event Manager</h1>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-3 py-1.5 bg-red-500 rounded-full hover:bg-red-600 transition-all duration-300 text-sm font-medium shadow-sm hover:scale-105"
          >
            Logout
          </button>
        </div>
      </nav>

{/* Sidebar */}
<div
  className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
>
  <button
    className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Dashboard"
    onClick={() => navigate('/dashboard')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Create Meeting"
    onClick={() => navigate('/create-meeting')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="View Meetings"
    onClick={() => navigate('/view-meetings')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Send Invites"
    onClick={() => navigate('/send-invites')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Search Meetings"
    onClick={() => navigate('/search-form')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Send Invite Without Pay"
    onClick={() => navigate('/adminsend')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
  </button>
  <button
    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
    title="Restrict Meeting"
    onClick={() => navigate('/restrict-meeting')}
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z"/>
    </svg>
    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
  </button>
</div>

      {/* Main Content */}
      <div className="pt-16 md:pl-24 p-4 max-w-4xl mx-auto transition-all duration-500">
        <h1 className="text-xl font-bold text-gray-900 mb-4 animate-fade-in">Send Invites to Existing Meeting</h1>
        {error && (
          <div className="mb-3 p-3 bg-red-50 text-red-600 rounded-lg border border-red-100 text-sm animate-slide-in">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-green-50 text-green-600 rounded-lg border border-green-100 text-sm animate-slide-in">
            {success}
          </div>
        )}
        <form onSubmit={handleSubmit} className="bg-white p-5 rounded-xl shadow-lg space-y-4 animate-fade-in">
          <div>
            <label className="block text-xs font-medium text-gray-700">Select Meeting <span className="text-red-500">*</span></label>
            <select
              value={selectedMeeting}
              onChange={handleMeetingChange}
              className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400"
              required
            >
              <option value="">Select a meeting</option>
              {meetings.map((meeting) => (
                <option key={meeting.id} value={meeting.id}>
                  {meeting.purpose} - {meeting.time} - {meeting.location}
                </option>
              ))}
            </select>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setInputMode('manual')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${inputMode === 'manual' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Manual Input
            </button>
            <button
              type="button"
              onClick={() => setInputMode('csv')}
              className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${inputMode === 'csv' ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              CSV Upload
            </button>
          </div>

          {inputMode === 'manual' && (
            <div className="animate-fade-in">
              {/* <h2 className="text-sm font-semibold text-gray-800 mb-3">Add Attendees</h2> */}
              {attendees.map((attendee, index) => (
                <div key={index} className="space-y-2 border p-3 rounded-lg mb-3 bg-gray-50 transition-all duration-300 hover:shadow-md">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={attendee.name}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={attendee.email}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Phone <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={attendee.phone}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="role"
                        value={attendee.role}
                        onChange={(e) => handleAttendeeChange(index, e)}
                        className="w-full px-2 py-1 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400"
                        required
                      >
                        <option value="">Select Role</option>
                        <option value="Admin">Admin</option>
                        <option value="Supervisor">Supervisor</option>
                      </select>
                    </div>
                  </div>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeAttendee(index)}
                      className="mt-2 px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-300 text-xs transform hover:scale-105"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {/* <button
                type="button"
                onClick={addAttendee}
                className="px-3 py-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all duration-300 text-sm transform hover:scale-105"
              >
                Add Attendee
              </button> */}
            </div>
          )}

          {inputMode === 'csv' && (
            <div className="border-t pt-3 animate-fade-in">
              <h2 className="text-sm font-semibold text-gray-800 mb-2">Upload Attendees via CSV</h2>
              <p className="text-gray-600 text-xs mb-2">Download the template, fill it with Name, Email, Phone, and Role, then upload.</p>
              <button
                type="button"
                onClick={downloadTemplate}
                className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-300 text-sm transform hover:scale-105"
              >
                Download CSV Template
              </button>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mt-3 block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 transition-all duration-300"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !selectedMeeting}
            className={`w-full py-2 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-md text-sm transform ${isSubmitting || !selectedMeeting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105'}`}
          >
            {isSubmitting ? 'Sending Invites...' : 'Send Invites'}
          </button>
        </form>
      </div>

      {/* CSV Confirmation Popup */}
      {showCsvPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-5 rounded-xl shadow-2xl max-w-2xl w-full max-h-[70vh] overflow-y-auto transform scale-95 animate-pop-in">
            <h2 className="text-sm font-bold mb-3">Confirm Uploaded Attendees</h2>
            {csvPreview && csvPreview.length > 0 ? (
              <>
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-teal-50">
                      <th className="border p-1.5 text-left font-medium">Name</th>
                      <th className="border p-1.5 text-left font-medium">Email</th>
                      <th className="border p-1.5 text-left font-medium">Phone</th>
                      <th className="border p-1.5 text-left font-medium">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((attendee, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-all duration-200">
                        <td className="border p-1.5">{attendee.name}</td>
                        <td className="border p-1.5">{attendee.email}</td>
                        <td className="border p-1.5">{attendee.phone}</td>
                        <td className="border p-1.5">{attendee.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 flex justify-end space-x-2">
                  <button
                    onClick={() => { setShowCsvPopup(false); setCsvFile(null); setCsvPreview(null); }}
                    className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-300 text-xs transform hover:scale-105"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmCsvUpload}
                    className="px-3 py-1.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all duration-300 text-xs transform hover:scale-105"
                  >
                    Confirm & Submit
                  </button>
                </div>
              </>
            ) : (
              <p className="text-xs">No valid data found in CSV.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SendInvites;